import { useState, useRef, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Target, Clock, Hash } from 'lucide-react'
import { cn } from '@/lib/utils'

interface JumpToFramePopoverProps {
  currentFrame: number
  totalFrames: number
  fps: number
  duration: number
  recentFrames?: number[]
  disabled?: boolean
  onJumpToFrame: (frame: number) => void
  onJumpToTime: (time: number) => void
}

// Parse timestamp string to seconds (supports MM:SS, HH:MM:SS, and SS formats)
function parseTimestamp(timestamp: string): number | null {
  const trimmed = timestamp.trim()
  
  // Try parsing as seconds (e.g., "45.5")
  if (/^\d+(\.\d+)?$/.test(trimmed)) {
    return parseFloat(trimmed)
  }
  
  // Try parsing as MM:SS or HH:MM:SS
  const parts = trimmed.split(':').map((p) => parseFloat(p.trim()))
  
  if (parts.some(isNaN)) return null
  
  if (parts.length === 2) {
    // MM:SS
    const [minutes, seconds] = parts
    return minutes * 60 + seconds
  }
  
  if (parts.length === 3) {
    // HH:MM:SS
    const [hours, minutes, seconds] = parts
    return hours * 3600 + minutes * 60 + seconds
  }
  
  return null
}

// Format seconds to timestamp string
function formatTimestamp(seconds: number): string {
  if (isNaN(seconds) || seconds < 0) return '00:00.000'
  
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  const secs = seconds % 60
  
  if (hours > 0) {
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toFixed(3).padStart(6, '0')}`
  }
  return `${minutes.toString().padStart(2, '0')}:${secs.toFixed(3).padStart(6, '0')}`
}

export function JumpToFramePopover({
  currentFrame,
  totalFrames,
  fps,
  duration,
  recentFrames = [],
  disabled = false,
  onJumpToFrame,
  onJumpToTime,
}: JumpToFramePopoverProps) {
  const [open, setOpen] = useState(false)
  const [frameInput, setFrameInput] = useState('')
  const [timeInput, setTimeInput] = useState('')
  const [activeTab, setActiveTab] = useState<'frame' | 'time'>('frame')
  const frameInputRef = useRef<HTMLInputElement>(null)
  const timeInputRef = useRef<HTMLInputElement>(null)

  // Focus input when popover opens
  useEffect(() => {
    if (open) {
      setTimeout(() => {
        if (activeTab === 'frame') {
          frameInputRef.current?.focus()
          frameInputRef.current?.select()
        } else {
          timeInputRef.current?.focus()
          timeInputRef.current?.select()
        }
      }, 50)
    }
  }, [open, activeTab])

  // Update inputs when current frame changes
  useEffect(() => {
    if (!open) {
      setFrameInput((currentFrame + 1).toString())
      setTimeInput(formatTimestamp(currentFrame / fps))
    }
  }, [currentFrame, fps, open])

  const handleFrameSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const frame = parseInt(frameInput, 10)
    if (!isNaN(frame) && frame >= 1 && frame <= totalFrames) {
      onJumpToFrame(frame - 1) // Convert to 0-indexed
      setOpen(false)
    }
  }

  const handleTimeSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const time = parseTimestamp(timeInput)
    if (time !== null && time >= 0 && time <= duration) {
      onJumpToTime(time)
      setOpen(false)
    }
  }

  const handleRecentFrameClick = (frame: number) => {
    onJumpToFrame(frame - 1) // Convert to 0-indexed
    setOpen(false)
  }

  // Validate frame input
  const frameValue = parseInt(frameInput, 10)
  const isFrameValid = !isNaN(frameValue) && frameValue >= 1 && frameValue <= totalFrames

  // Validate time input
  const timeValue = parseTimestamp(timeInput)
  const isTimeValid = timeValue !== null && timeValue >= 0 && timeValue <= duration

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          className="gap-2"
          title="Jump to frame or timestamp"
        >
          <Target className="h-4 w-4" />
          <span className="hidden sm:inline">Jump to</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="start">
        <div className="space-y-4">
          <div className="font-medium">Jump To</div>

          <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'frame' | 'time')}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="frame" className="gap-2">
                <Hash className="h-3 w-3" />
                Frame
              </TabsTrigger>
              <TabsTrigger value="time" className="gap-2">
                <Clock className="h-3 w-3" />
                Time
              </TabsTrigger>
            </TabsList>

            <TabsContent value="frame" className="space-y-3">
              <form onSubmit={handleFrameSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="frame-input">Frame Number</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={frameInputRef}
                      id="frame-input"
                      type="number"
                      min={1}
                      max={totalFrames}
                      value={frameInput}
                      onChange={(e) => setFrameInput(e.target.value)}
                      placeholder={`1 - ${totalFrames}`}
                      className={cn(
                        'flex-1',
                        frameInput && !isFrameValid && 'border-destructive'
                      )}
                    />
                    <Button type="submit" disabled={!isFrameValid}>
                      Go
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Range: 1 - {totalFrames.toLocaleString()}
                  </p>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="time" className="space-y-3">
              <form onSubmit={handleTimeSubmit}>
                <div className="space-y-2">
                  <Label htmlFor="time-input">Timestamp</Label>
                  <div className="flex gap-2">
                    <Input
                      ref={timeInputRef}
                      id="time-input"
                      type="text"
                      value={timeInput}
                      onChange={(e) => setTimeInput(e.target.value)}
                      placeholder="MM:SS or HH:MM:SS"
                      className={cn(
                        'flex-1',
                        timeInput && !isTimeValid && 'border-destructive'
                      )}
                    />
                    <Button type="submit" disabled={!isTimeValid}>
                      Go
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Duration: {formatTimestamp(duration)}
                  </p>
                </div>
              </form>
            </TabsContent>
          </Tabs>

          {/* Recent frames */}
          {recentFrames.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Recent Frames</Label>
              <div className="flex flex-wrap gap-1">
                {recentFrames.slice(0, 8).map((frame) => (
                  <Button
                    key={frame}
                    variant="secondary"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => handleRecentFrameClick(frame)}
                  >
                    {frame}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
